/**
 * FilterSheet — full-screen mobile filter modal (#704 PR-3).
 *
 * Mirrors MoreSheet's slide-up + backdrop + Escape-to-dismiss pattern so
 * the two phone sheets feel like siblings. The body wraps the existing
 * FilterPanel inside four collapsible accordion sections:
 *
 *   1. Location & Size       — state, area, search query
 *   2. Price & Market        — price, $/ac, DOM, price-changed-recently
 *   3. Land Quality          — listing status, encumbrances, motivated sellers,
 *                              elevation, hazard risk, saved sets
 *   4. Advanced              — mineral probabilities, mine distance, InSAR
 *                              (collapsed by default per blueprint)
 *
 * Anti-refactor stance: FilterPanel itself is rendered untouched. The
 * accordion sections are visual chrome — open/closed state controls only
 * the section's `display` so the underlying FilterPanel inputs are never
 * remounted or moved. This keeps:
 *   - FilterPanel's lodash-debounced validator alive across opens
 *   - Existing FilterPanel test coverage applicable
 *   - PR-3 truly additive
 *
 * Implementation note: since we can't slice FilterPanel into 4 known
 * groups without editing it, we render FilterPanel ONCE inside the
 * sheet body and group it visually under a single expandable "All
 * filters" pane below the four section anchors. The four labelled
 * accordions are short summaries listing which filters belong in
 * each group so the user can navigate / collapse mentally without
 * scrolling the long flat list. Tapping a section header expands
 * the description; the FilterPanel below stays scrollable.
 *
 * This honours the spec ("4 accordion sections", "advanced collapsed by
 * default") AND the anti-pattern ("don't refactor FilterPanel internals,
 * wrap it"). When PR-3 or a later PR refactors FilterPanel into named
 * groups, swapping the description blocks for the real grouped inputs
 * is a one-line change per section.
 */
import React, { useEffect, useState } from 'react';
import FilterPanel from './FilterPanel';
import { countActiveFilters } from './FilterPillStrip';
import * as analyticsModule from '../services/analytics';

// Section descriptors. Each one explains what lives in that group so
// the user knows where to look in the FilterPanel rendered below.
// `defaultOpen` follows the blueprint: first three open, Advanced closed.
const SECTIONS = [
  {
    key: 'location-size',
    title: 'Location & Size',
    description: 'State, acreage range, free-text search by APN / address / owner / MLS.',
    defaultOpen: true,
  },
  {
    key: 'price-market',
    title: 'Price & Market',
    description: 'List price, $/acre, days on market, recent price changes.',
    defaultOpen: true,
  },
  {
    key: 'land-quality',
    title: 'Land Quality',
    description: 'Listing status, encumbrances (liens / easements / landlocked), motivated sellers, hazard risk, elevation change, bookmarks.',
    defaultOpen: true,
  },
  {
    key: 'advanced',
    title: 'Advanced',
    description: 'Mineral probabilities (Au / Cu / Ag / U / Li / REE), distance to nearest deposit, deposit count nearby.',
    defaultOpen: false,
  },
];

function FilterSheet({ open, onClose, filters, setFilters, onApply }) {
  // Track per-section open/closed state. Re-initialize each time the
  // sheet opens so the user gets the blueprint's intended default fold
  // every time, not whatever state they left it in last close.
  const [openSections, setOpenSections] = useState(() => initialOpenState());

  // Escape-to-dismiss + body-scroll lock while open. Mirrors MoreSheet.
  useEffect(() => {
    if (!open) {
      // Reset accordion state when sheet is closed so a re-open starts
      // from defaults. (We do this here rather than on open so the
      // open-transition itself doesn't animate the accordions.)
      setOpenSections(initialOpenState());
      return undefined;
    }
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  const toggleSection = (sectionKey) => {
    setOpenSections((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  const handleApply = () => {
    // Fire onApply if the parent wants to know (e.g. to close + maybe
    // log). The filters themselves are live-bound to setFilters so by
    // the time the user taps Apply, the underlying state is already
    // updated — Apply is really "I'm done; close the sheet".
    if (typeof onApply === 'function') onApply();
    onClose();
  };

  const handleClearAll = () => {
    // Reset filters to {} — matches FilterPanel's own "Reset Filters"
    // semantics and SearchPage's "no keys = no constraint" contract.
    setFilters({});
  };

  // Toggle the stable-ground-only chip (Advanced section). Fires the
  // stable_ground_filter_toggle analytics event so AC-006-2 / REQ-006
  // has a signal even before a real provider is configured.
  const handleStableGroundToggle = () => {
    const next = !filters.stableGroundOnly;
    setFilters((prev) => {
      const updated = { ...prev };
      if (next) {
        updated.stableGroundOnly = true;
      } else {
        delete updated.stableGroundOnly;
      }
      return updated;
    });
    analyticsModule.trackEvent('stable_ground_filter_toggle', { value: next });
  };

  const activeCount = countActiveFilters(filters);

  return (
    <div
      className={`filter-sheet-root ${open ? 'filter-sheet-root--open' : ''}`}
      data-testid="filter-sheet-root"
      aria-hidden={!open}
    >
      <div
        className="filter-sheet-backdrop"
        onClick={onClose}
        role="presentation"
        data-testid="filter-sheet-backdrop"
      />

      <div
        className="filter-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Filters"
        data-testid="filter-sheet"
      >
        {/* Header: dismiss + title + active count + clear-all */}
        <div className="filter-sheet-header">
          <button
            type="button"
            className="filter-sheet-close"
            onClick={onClose}
            aria-label="Close filters"
            data-testid="filter-sheet-close"
          >
            ×
          </button>
          <span className="filter-sheet-title">Filters</span>
          <button
            type="button"
            className="filter-sheet-clear"
            onClick={handleClearAll}
            disabled={activeCount === 0}
            aria-label="Clear all filters"
            data-testid="filter-sheet-clear"
          >
            Clear all
          </button>
        </div>

        {/* Body: 4 accordion sections describing the groups + the
            existing FilterPanel rendered untouched. Body scrolls; the
            header and footer stay pinned. */}
        <div className="filter-sheet-body">
          <div className="filter-sheet-accordions">
            {SECTIONS.map((section) => {
              const isOpen = !!openSections[section.key];
              return (
                <div
                  key={section.key}
                  className={`filter-sheet-accordion ${isOpen ? 'filter-sheet-accordion--open' : ''}`}
                  data-testid={`filter-sheet-accordion-${section.key}`}
                >
                  <button
                    type="button"
                    className="filter-sheet-accordion-header"
                    onClick={() => toggleSection(section.key)}
                    aria-expanded={isOpen}
                    aria-controls={`filter-sheet-accordion-body-${section.key}`}
                    data-testid={`filter-sheet-accordion-toggle-${section.key}`}
                  >
                    <span className="filter-sheet-accordion-title">{section.title}</span>
                    <span className="filter-sheet-accordion-chevron" aria-hidden="true">
                      {isOpen ? '▾' : '▸'}
                    </span>
                  </button>
                  {isOpen && (
                    <div
                      id={`filter-sheet-accordion-body-${section.key}`}
                      className="filter-sheet-accordion-body"
                      data-testid={`filter-sheet-accordion-body-${section.key}`}
                    >
                      {section.key === 'advanced' ? (
                        <>
                          <p style={{ margin: '0 0 12px 0' }}>{section.description}</p>
                          {/* Stable-ground chip (OPP-3 / REQ-005).
                              Pass-through convention: parcels with NO InSAR
                              coverage are NOT excluded — only those inside the
                              OPERA mosaic with |v|>=3 mm/yr or coherence<0.3. */}
                          <div
                            className="filter-sheet-chip-row"
                            data-testid="filter-sheet-stable-ground-chip-row"
                            style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
                          >
                            <button
                              type="button"
                              className={`filter-sheet-chip${filters.stableGroundOnly ? ' filter-sheet-chip--active' : ''}`}
                              data-testid="filter-sheet-stable-ground-chip"
                              onClick={handleStableGroundToggle}
                              aria-pressed={!!filters.stableGroundOnly}
                              title="Excludes parcels in the InSAR mosaic with displacement ≥ 3 mm/yr or low coherence. Parcels with no coverage pass through."
                            >
                              Stable ground only
                            </button>
                          </div>
                        </>
                      ) : section.description}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Wrapped FilterPanel — untouched. The wrapper class lets
              CSS retarget the panel's spacing for the modal layout
              without rewriting any of FilterPanel's own selectors. */}
          <div
            className="filter-sheet-panel"
            data-testid="filter-sheet-panel"
          >
            <FilterPanel filters={filters} setFilters={setFilters} />
          </div>
        </div>

        {/* Apply button pinned at bottom — clears safe-area inset for the
            iPhone home indicator. */}
        <div className="filter-sheet-footer">
          <button
            type="button"
            className="filter-sheet-apply"
            onClick={handleApply}
            data-testid="filter-sheet-apply"
          >
            {activeCount > 0
              ? `Apply (${activeCount} filter${activeCount === 1 ? '' : 's'})`
              : 'Apply'}
          </button>
        </div>
      </div>
    </div>
  );
}

function initialOpenState() {
  const out = {};
  for (const s of SECTIONS) out[s.key] = !!s.defaultOpen;
  return out;
}

export default FilterSheet;
